import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { useRef } from "react";
import { resolvedCtaUrl } from "@/journey/externalNavigation";
import { demoRegistry, getDemo, nextDemoId } from "@/demos/registry";
import type { DemoAction, DemoControlState, DemoId } from "@/demos/types";
import { DemoControls } from "@/screens/DemoControls";
import type { RuntimeMode } from "@/runtime/types";

gsap.registerPlugin(SplitText);

export interface DemoHudProps {
  activeDemoId: DemoId;
  mode: RuntimeMode;
  controls: DemoControlState;
  notice: string | null;
  loadingNext: boolean;
  reducedMotion: boolean;
  onAction: (action: DemoAction) => void;
  onReset: () => void;
  onReposition: () => void;
  onSelectDemo: (id: DemoId) => void;
  onNextDemo: () => void;
  onRestart: () => void;
  onVisit: () => void;
}

/**
 * Wireframe frames 5-8. The completion state (frame 8) is frame 7 with the
 * card flipped, so the CTA and Restart appear on the same HUD rather than in a
 * separate screen.
 */
export function DemoHud(props: DemoHudProps) {
  const demo = getDemo(props.activeDemoId);
  const completed = props.activeDemoId === "engage" && props.controls.engageSide === "back";
  const titleRef = useRef<HTMLHeadingElement>(null);
  const hintRef = useRef<HTMLParagraphElement>(null);

  useGSAP(() => {
    const title = titleRef.current;
    const hint = hintRef.current;
    if (!title || !hint || props.reducedMotion) return;

    const split = SplitText.create(title, {
      type: "chars",
      charsClass: "chapter-title-char",
    });
    let splitReverted = false;
    const revertSplit = () => {
      if (splitReverted) return;
      split.revert();
      splitReverted = true;
    };
    const timeline = gsap.timeline();
    timeline
      .from(split.chars, {
        duration: 0.5,
        opacity: 0,
        // scale: 0,
        y: 22,
        rotationY: -90,
        // transformOrigin: "50% 50% -20px",
        ease: "back.out(1.7)",
        stagger: 0.065,
        onComplete: revertSplit,
      })
      .fromTo(hint, { opacity: 0 }, { opacity: 1, duration: 0.45, ease: "power1.out" });

    return revertSplit;
  }, { dependencies: [props.activeDemoId, props.reducedMotion], revertOnUpdate: true });

  return (
    <section className="demo-hud" data-testid="demo-hud" data-demo={props.activeDemoId}>
      <div key={`chapter-heading-${props.activeDemoId}`} className="chapter-heading">
        <h1 ref={titleRef} aria-label={demo.title}>{demo.title}</h1>
        <p ref={hintRef}>{demo.hint}</p>
      </div>

      {props.notice ? <p className="mode-notice" role="status" aria-live="polite">{props.notice}</p> : null}

      <nav className="utility-controls" aria-label="Experience controls" data-ui-control>
        {/* <button className="icon-button" type="button" onClick={props.onReset} aria-label="Reset this demo" title="Reset">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 12a8 8 0 1 1-2.34-5.66" />
            <path d="M20 4v4.5h-4.5" />
          </svg>
        </button> */}
        {/* Repositioning only means something once the world is being tracked. */}
        {props.mode === "webxr" ? (
          <button
            className="icon-button"
            type="button"
            onClick={props.onReposition}
            aria-label="Reposition experience"
            title="Reposition"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 12a8 8 0 1 1-2.34-5.66" />
              <path d="M20 4v4.5h-4.5" />
            </svg>
          </button>
        ) : null}
      </nav>

      <DemoControls
        key={`demo-controls-${props.activeDemoId}`}
        id={props.activeDemoId}
        controls={props.controls}
        dispatch={props.onAction}
      />

      <div className="experience-dock" data-ui-control>
        {/*
          The steps are buttons, not decoration. Without this, going back one
          stage means cycling forward through the whole set — the indicator
          shows you where you are while refusing to take you there.
        */}
        <ol className="demo-progress" aria-label="Experience progress">
          {demoRegistry.map((entry) => (
            <li key={entry.id} aria-current={entry.id === props.activeDemoId ? "step" : undefined}>
              <button
                type="button"
                disabled={props.loadingNext || entry.id === props.activeDemoId}
                onClick={() => props.onSelectDemo(entry.id)}
                aria-label={`Go to ${entry.title}`}
              >
                {entry.title}
              </button>
            </li>
          ))}
        </ol>
        <div className="primary-actions">
          {completed ? (
            <>
              {/* <button className="button-quiet" type="button" onClick={props.onRestart} data-testid="restart">
                Restart demo
              </button> */}
              <a
                className="cta-button"
                href={resolvedCtaUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={props.onVisit}
                data-testid="cta-link"
              >
                Visit Rising Pixel <span aria-hidden="true">→</span>
              </a>
            </>
          ) : (
            <button
              className="next-button"
              type="button"
              onClick={props.onNextDemo}
              disabled={props.loadingNext}
              aria-label={`Next: ${getDemo(nextDemoId(props.activeDemoId)).title}`}
              data-testid="next-demo"
            >
              <span>{props.loadingNext ? "Loading" : "Next"}</span><i aria-hidden="true">→</i>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
